import React, { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { trackEvent } from "../lib/analytics";
import { broadcastSignup } from "../lib/signupBus";
import { BACKEND_URL } from "../lib/config";

const SESSION_KEY = "xandi_exit_intent_shown";
const SIGNUP_KEY = "xandi_signed_up";

const ExitIntentModal = () => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState({ tone: "", text: "" });
  const triggered = useRef(false);

  useEffect(() => {
    // Don't arm if already shown this session or already signed up.
    if (sessionStorage.getItem(SESSION_KEY)) return;
    if (localStorage.getItem(SIGNUP_KEY)) return;
    // Desktop only: skip on touch devices / small screens.
    const isTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;
    if (isTouch || window.innerWidth < 900) return;

    const onLeave = (e) => {
      if (triggered.current) return;
      // Mouse moving toward the top of the viewport (toward the close tab / URL bar).
      if (e.clientY <= 2 && e.relatedTarget == null) {
        triggered.current = true;
        sessionStorage.setItem(SESSION_KEY, "1");
        setOpen(true);
        trackEvent("Exit Intent Shown");
      }
    };

    // Listen on documentElement; small delay to avoid firing on initial mouse jitter.
    const armTimer = setTimeout(() => {
      document.documentElement.addEventListener("mouseleave", onLeave);
    }, 4000);

    return () => {
      clearTimeout(armTimer);
      document.documentElement.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  const close = () => setOpen(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg({ tone: "", text: "" });
    try {
      const refParam = new URLSearchParams(window.location.search).get("ref");
      const res = await fetch(`${BACKEND_URL}/api/launch-signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          role: "customer",
          referred_by: refParam,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        const isNew = data.status !== "already_subscribed";
        if (isNew) {
          trackEvent("Waitlist Signup", { role: "customer", source: "exit_intent" });
          trackEvent("Exit Intent Signup");
        }
        localStorage.setItem(SIGNUP_KEY, "1");
        if (data.ref_code) {
          broadcastSignup({
            ref_code: data.ref_code,
            referral_count: data.referral_count || 0,
            email: email.trim(),
            role: "customer",
          });
        }
        setMsg({
          tone: "ok",
          text: isNew
            ? "You're in! Check your email for WELCOME15. 🎉"
            : "You're already on the list — see you Dec 1! 💕",
        });
        // Auto-close + scroll to referral panel after a beat.
        setTimeout(() => {
          setOpen(false);
          const el = document.getElementById("join");
          if (el) el.scrollIntoView({ behavior: "smooth" });
        }, 1400);
      } else {
        setMsg({
          tone: "err",
          text:
            data.detail && typeof data.detail === "string"
              ? data.detail
              : "Please enter a valid email address.",
        });
      }
    } catch (err) {
      setMsg({ tone: "err", text: "Something went wrong. Please try again." });
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="exit-overlay" data-testid="exit-intent-overlay" onClick={close}>
      <div
        className="exit-modal"
        data-testid="exit-intent-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Get WELCOME15 before you go"
      >
        <button
          type="button"
          className="exit-close"
          onClick={close}
          aria-label="Close"
          data-testid="exit-intent-close"
        >
          <X size={20} />
        </button>
        <p className="exit-eyebrow">Wait — before you go</p>
        <h3 className="exit-title">
          Grab <span>WELCOME15</span> on your first booking.
        </h3>
        <p className="exit-sub">
          Drop your email and we'll send your 15%-off code the moment Xandi opens on Dec 1.
        </p>
        <form className="exit-form" onSubmit={onSubmit} data-testid="exit-intent-form">
          <input
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            data-testid="exit-intent-email"
          />
          <button
            type="submit"
            className="btn btn-primary"
            disabled={busy}
            data-testid="exit-intent-submit"
          >
            {busy ? "Joining…" : "Send me WELCOME15"}
          </button>
        </form>
        <div
          className={`exit-msg ${msg.tone}`}
          data-testid="exit-intent-message"
          aria-live="polite"
        >
          {msg.text}
        </div>
        <p className="exit-fine">No spam. Unsubscribe anytime. DPA-2020 compliant.</p>
      </div>
    </div>
  );
};

export default ExitIntentModal;
